import * as Sentry from '@sentry/vue';
import axios from 'axios';
import { appInDevMode, getAppEnvFromHost } from '../utils/helpers';
import { isChunkLoadError } from './chunk-error';
import { isNoisyPiniaBreadcrumb } from './pinia-state';

/**
 * Matched by Sentry's event filter against the innermost frame of the root exception only, so this
 * catches errors thrown inside a vendor script but not ones that merely pass through it.
 */
export const sentryDenyUrls = [
	/termly\.io/i,
	/nudgify\.com/i,
	/desk\.zoho\.com/i,
	/zohohcasapweb/i,
	/zohocdn\.com/i,
	/zohogcchat/i,
	/murphysdk/i, // Zoho's own error-monitoring SDK, loaded by the Desk widget
	/^iabjs:\/\//, // scripts the Instagram and Facebook in-app browsers inject
];

/**
 * Matched by Sentry's event filter against the event message and the last exception value, both as
 * `value` and as `Type: value`.
 *
 * The SDK's fetch instrumentation appends the request host to a failed fetch's message, e.g.
 * "Failed to fetch (a.klaviyo.com)", in each engine's wording (Chrome, Safari, Firefox). These
 * cannot be relied on to reach `denyUrls`: in Chrome the innermost frame is the SDK's own fetch
 * wrapper, not the vendor script that called it, and a stackless failure has no frame at all.
 *
 * "Java object is gone" is the Android WebView JS-bridge error Instagram and Facebook raise when a
 * page outlives the native object their injected script talks to. We expose no Java bridge.
 */
export const sentryIgnoreErrors = [
	/(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.) \((?:[\w-]+\.)*(?:klaviyo|nudgify)\.com\)/,
	/Java object is gone/,
];

/**
 * Path fragment every asset this app builds is served from.
 *
 * laravel-vite-plugin sets Vite's `base` to `${ASSET_URL}/build/` (its `buildDirectory` defaults to
 * 'build') and Vite's `build.assetsDir` defaults to 'assets', neither of which vite.config.mjs
 * overrides, so our chunks always resolve to `<origin-or-CDN>/build/assets/<name>-<hash>.js`.
 * Matched as a substring rather than a prefix so it keeps working when ASSET_URL moves assets onto
 * a CDN. A test asserts vite.config.mjs has not moved this out from under us.
 */
export const APP_ASSET_PATH = '/build/assets/';

/**
 * Name of the chunk vite.config.mjs splits the Sentry SDK into (`manualChunks`).
 *
 * EN-1129: the SDK wraps addEventListener, setTimeout, requestAnimationFrame, fetch and XHR, so its
 * wrapper frames sit in the stack of any vendor error raised through them. Those frames are served
 * from APP_ASSET_PATH and carry debug IDs like the rest of our bundle, and minification strips the
 * `sentryWrapped` name the SDK would otherwise strip them by - so without a chunk of its own, every
 * vendor error that passed through a wrapper looked first-party. Matched with APP_ASSET_PATH as a
 * prefix so our own `errors/sentry.js` chunk (`sentry-<hash>.js`) is never mistaken for it.
 */
export const SENTRY_SDK_CHUNK = 'vendor-sentry';

/**
 * True for a file URL that belongs to the Sentry SDK chunk.
 */
export const isSentrySdkFile = (file) => {
	return typeof file === 'string' && file.includes(`${APP_ASSET_PATH}${SENTRY_SDK_CHUNK}-`);
};

const isAppFile = (file) => {
	return typeof file === 'string' && file.includes(APP_ASSET_PATH) && !isSentrySdkFile(file);
};

/**
 * Share of filtered events kept as an audit sample, so we can see what the filter eats without
 * carrying its volume. Lower to 0.02 once EN-958 is verified in production, then to 0.
 */
const FILTERED_SAMPLE_RATE = 0.1;

/**
 * True when any frame in the event belongs to code this app built, other than the Sentry SDK.
 *
 * Two independent signals, unioned so either one failing still leaves us reporting:
 *
 * 1. `debug_meta.images` - @sentry/vite-plugin injects a debug ID into every chunk it emits
 *    (independently of whether an auth token is configured), and @sentry/core moves the ID of each
 *    frame's file into `debug_meta` before beforeSend runs, one image per file with its URL as
 *    `code_file`. Self-calibrating: it follows any change to the build directory or CDN.
 * 2. APP_ASSET_PATH in a frame filename - independent of the Sentry build plugin, so this still
 *    holds if that plugin is ever removed.
 *
 * Both skip the SDK chunk (see SENTRY_SDK_CHUNK). Debug IDs live in a page-wide global, so a vendor
 * script built with a Sentry bundler plugin of its own would also produce an image here; that keeps
 * the event, which is the safe way to be wrong.
 *
 * Checks every exception value rather than only the first, so a third-party cause chained onto a
 * first-party error is still recognised as ours.
 */
export const hasAppFrame = (event) => {
	if (event?.debug_meta?.images?.some((image) => !isSentrySdkFile(image?.code_file))) {
		return true;
	}

	return event?.exception?.values?.some(
		(value) => value?.stacktrace?.frames?.some((frame) => isAppFile(frame?.filename))
	) === true;
};

export const isNativeEventRejection = (error) => {
	return error instanceof Event && !(error instanceof ErrorEvent);
};

/**
 * True for a browser-raised error whose stack contains none of our own code.
 *
 * This is the EN-958 filter. Third-party beacons (Klaviyo, Termly, Nudgify, Clarity, Zoho) fire
 * requests we cannot wrap and discard the promises they return, so every ad-blocked, offline or
 * ITP-killed request becomes an unhandled rejection. The browser builds those TypeErrors inside
 * networking with no JS stack at all, which is also why `denyUrls` cannot reach them - it needs a
 * frame to read a URL from.
 *
 * Three gates, all required, each failing open (keep the event) when it cannot decide:
 *
 * 1. An `exception` must be present. captureMessage() events also reach beforeSend, because the SDK
 *    treats any event with `type === undefined` as an error event, and they carry only
 *    `event.message` - so without this gate the frame test would silently drop every message we
 *    deliberately send.
 * 2. Some value's `mechanism.handled` must be exactly false, which only the SDK's own
 *    instrumentation sets: the global onerror and onunhandledrejection handlers, the wrappers
 *    around listeners and timers, and @sentry/vue's vue-router hook (any navigation error in the
 *    SPA). captureException() and the Vue errorHandler in errors/utils.js
 *    produce `handled: true` and are always kept, even when the error was thrown entirely inside a
 *    third-party script we called into. Any value rather than a fixed one: when the error has a
 *    `.cause` (or is an AggregateError), the LinkedErrors integration prepends an entry per cause
 *    *before* the real mechanism is applied, and the SDK applies it to the first entry - so it lands
 *    on the deepest cause, while the originally-thrown error, last, is stamped `handled: true`.
 *    Every entry LinkedErrors synthesises defaults to `handled: true`, so no other value can
 *    produce a false positive here.
 * 3. No frame belongs to our own bundle.
 */
export const isUnhandledThirdPartyError = (event) => {
	const values = event?.exception?.values;

	if (!values?.length) {
		return false;
	}

	if (!values.some((value) => value?.mechanism?.handled === false)) {
		return false;
	}

	return !hasAppFrame(event);
};

/**
 * True for an ApiError (api/client.js) carrying one of Cloudflare's own 52x origin errors - the
 * origin timed out, refused, or answered with something Cloudflare could not use. The browser can
 * do nothing about these, and they belong to server-side and Cloudflare monitoring.
 *
 * Duck-typed rather than `instanceof`, because api/client.js imports this module.
 */
export const isCloudflareOriginError = (error) => {
	return error?.name === 'ApiError'
		&& Number.isInteger(error?.status)
		&& error.status >= 520
		&& error.status <= 527;
};

/**
 * Drops an event, keeping a small random sample so the filter stays observable in production -
 * otherwise an over-eager filter looks exactly like a healthy quiet day.
 *
 * Sampled events are downgraded to `info` and fingerprinted onto one audit issue per reason, so
 * they are a single row to inspect rather than noise back in the error stream.
 */
const dropFilteredEvent = (event, reason) => {
	if (Math.random() >= FILTERED_SAMPLE_RATE) {
		return null;
	}

	event.level = 'info';
	event.fingerprint = ['sentry-filter-audit', reason];
	event.tags = { ...event.tags, filtered_reason: reason, filtered_sample: 'true' };

	return event;
};

/**
 * Order matters. The chunk-load branch is a terminal decision that has to stay ahead of the
 * third-party filter: Safari reports a failed module load with an empty stack, which has no app
 * frame, so the filter below would otherwise swallow chunk errors. Deciding it here also makes
 * EN-955 - which flips chunk errors from dropped to reported - a one-line change.
 *
 * The Cloudflare branch needs to exist because the third-party filter can never catch an ApiError:
 * it is built inside our bundle, so it always has an app frame. Nor can the axios branch - ApiError
 * wraps the AxiosError rather than being one.
 */
export const sentryBeforeSend = (event, hint) => {
	const error = hint?.originalException;

	if (error && axios.isAxiosError(error)) {
		return null;
	}

	if (isCloudflareOriginError(error)) {
		return dropFilteredEvent(event, 'cloudflare-52x');
	}

	if (isChunkLoadError(error)) {
		return null;
	}

	if (isNativeEventRejection(error)) {
		return dropFilteredEvent(event, 'native-event-rejection');
	}

	if (isUnhandledThirdPartyError(event)) {
		return dropFilteredEvent(event, 'no-app-frame');
	}

	return event;
};

/**
 * EN-961: attachments are appended to the envelope by `sendEvent` AFTER `beforeSend` has run,
 * reading the same `hint` object, so clearing them here actually drops them. They bypass every
 * other control we have - `normalizeDepth`, `normalizeMaxBreadth`, `maxValueLength` and
 * `beforeSend` itself - which is why the Pinia plugin runs with `attachPiniaState: false` and
 * why this refuses any state attachment unconditionally as a second line of defence.
 */
const stripPiniaStateAttachments = (hint) => {
	if (!hint?.attachments?.length) {
		return;
	}

	hint.attachments = hint.attachments.filter(
		(attachment) => !attachment?.filename?.startsWith('pinia_state_')
	);
};

/**
 * Everything both Vite entries share: transport, environment, release, sampling and the event
 * filters. The SPA (resources/js/app.js) and the Inertia entry (resources/js/inertia/app.js)
 * differ only in how navigation is instrumented and when the Vue app joins. Identity is not
 * derived here - EN-960's `syncSentryUser` (errors/sentry-user.js) is pushed onto the Sentry
 * scope directly by whichever entry owns the source of truth (composables/auth.js for the SPA,
 * the `auth.user` prop watcher for Inertia), so both entries can share this options object as-is.
 */
const buildSentryOptions = () => ({
	dsn: import.meta.env.VITE_SENTRY_VUE_DSN,
	denyUrls: sentryDenyUrls,
	ignoreErrors: sentryIgnoreErrors,
	environment: getAppEnvFromHost(),
	release: import.meta.env.VITE_APP_VERSION,
	enabled: !appInDevMode(),
	enableLogs: !appInDevMode(),
	// EN-961: the Pinia state context nests to contexts.state.state.value.<storeId>.<field>,
	// and the plugin grants that subtree `3 + normalizeDepth` levels. Set explicitly so the
	// coupling is visible. `normalizeMaxBreadth` is lowered from the default 1000 to bound
	// any future mistake cheaply.
	//
	// `maxValueLength` is deliberately NOT set: it truncates only `request.url` and
	// `exception.values[].value`, never contexts, so it cannot bound anything here. Nor can
	// `normalizeDepth` - `normalize()` returns strings unchanged at any depth. The only
	// thing that trims a long string in a context is the stateTransformer in pinia-state.js.
	normalizeDepth: 4,
	normalizeMaxBreadth: 200,
	sendDefaultPii: false,
	beforeBreadcrumb(breadcrumb) {
		return isNoisyPiniaBreadcrumb(breadcrumb) ? null : breadcrumb;
	},
	beforeSend(event, hint) {
		stripPiniaStateAttachments(hint);

		return sentryBeforeSend(event, hint);
	},
	tracesSampleRate: 0.1,
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 0.1,
});

/**
 * SPA entry (resources/js/app.js). vue-router drives navigation spans.
 */
export const initSentry = (app, router) => {
	Sentry.init({
		...buildSentryOptions(),
		app,
		integrations: [
			Sentry.browserTracingIntegration({ router }),
		],
	});
};

/**
 * Inertia entry (resources/js/inertia/app.js). Runs at module load, before the first page component
 * resolves. A stale-chunk reload is a full page load, so "chunk reload exhausted" is always reported
 * from an initial resolve that failed, and then createInertiaApp's setup() - where the app is
 * created - never runs. The Vue integration joins later through attachSentryToApp(). It is dropped
 * from the defaults here because @sentry/vue's init() adds one, and a second integration with the
 * same name is skipped.
 *
 * There is no vue-router. Navigation spans come from Inertia's `navigate` event
 * (bootstrap/sentry.js), named after the page component, so the History API instrumentation that
 * would name them by URL is off.
 */
export const initInertiaSentry = () => {
	Sentry.init({
		...buildSentryOptions(),
		integrations: (defaults) => [
			...defaults.filter((integration) => integration.name !== 'Vue'),
			Sentry.browserTracingIntegration({ instrumentNavigation: false }),
		],
	});
};

/**
 * Attach the Vue integration to the Inertia entry's app. Call before app.mount(), and before
 * bootstrap/errors.js installErrorHandlers(), so our handler replaces the one Sentry wraps in.
 *
 * Only on an enabled client, mirroring init(), which sets up no integrations when Sentry is off.
 */
export const attachSentryToApp = (app) => {
	if (Sentry.isEnabled()) {
		Sentry.getClient().addIntegration(Sentry.vueIntegration({ app }));
	}
};

export { createPiniaSentryPlugin } from './pinia-state';
export default Sentry;
